import {
  AffineCoefficients,
  GeoCoordinate,
  MapRegion,
  Point,
  Triangle,
  UpmPlantGeoData
} from '../../types'

import {calculateAffineCoefficients, findBestTriangle, transformGeoToSvg} from './affineTransform'
import {getRegionConfig} from './configs'
import {spreadOverlappingMarkers} from './spreadMarkers'

const coefficientCache = new Map<string, AffineCoefficients>()

const getTriangleCoefficients = (triangle: Triangle): AffineCoefficients => {
  const cached = coefficientCache.get(triangle.name)
  if (cached) return cached

  const coefficients = calculateAffineCoefficients(triangle.points)
  coefficientCache.set(triangle.name, coefficients)
  return coefficients
}

/**
 * Calculate SVG locations for plants.
 *
 * @param region - Map region identifier (determines which transformation to use)
 * @param plants - Array of plant data with geo coordinates (pre-filtered by caller)
 * @returns Record of plantId → SVG Point
 */
export const calculatePlantLocations = (
  region: MapRegion,
  plants: UpmPlantGeoData[]
): Record<string, Point> => {
  const config = getRegionConfig(region)
  if (!config) {
    console.warn(`[GeoTransform] No configuration for region: ${region}`)
    return {}
  }

  const locationMap: Record<string, Point> = {}

  for (const plant of plants) {
    const {latitude, longitude} = plant
    if (!latitude || !longitude) continue

    const geo: GeoCoordinate = {
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude)
    }
    if (isNaN(geo.latitude) || isNaN(geo.longitude)) continue

    const bestTriangle = findBestTriangle(geo, config.triangles)
    const coefficients = getTriangleCoefficients(bestTriangle)
    locationMap[plant.plantId] = transformGeoToSvg(geo, coefficients)
  }

  return spreadOverlappingMarkers(locationMap)
}
